import "./locationPhysicianDetailsEditComponent.scss";
import SelectDropdownComponent from "../../../../../shared/components/core/select-dropdown/SelectDropdownComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";
import AccordionComponent from "../../../../../shared/components/accordion/accordionComponent";
import CommonService from "../../../../../shared/services/common.service";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import PhysicianDetailsComponent from "../../../../../shared/components/physician-details/PhysicianDetailsComponent";
import React, {useCallback, useEffect, useState} from "react";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import _ from "lodash";
import {ImageConfig} from "../../../../../constants";

interface LocationPhysicianDetailsEditComponentProps {
    physicianDetailsList?: any;
    locationId?: any;
    onUpdateComplete?: Function;
    facilityId: any;
    onEditCancel: Function;
}

const LocationPhysicianDetailsEditComponent = (props: LocationPhysicianDetailsEditComponentProps) => {
    const {locationId, onUpdateComplete, physicianDetailsList, onEditCancel, facilityId} = props;

    const [physicianId, setPhysicianId] = useState([]);
    const [isLinking, setIsLinking] = useState(false);
    const [completePhysicianList, setCompletePhysicianList] = useState<any>([]);
    const [physicianList, setPhysicianList] = useState<any>([]);
    const [isPhysicianListLoading, setIsPhysicianListLoading] = useState<boolean>(false);
    const [isPhysicianListLoaded, setIsPhysicianListLoaded] = useState<boolean>(false);
    const [isPhysicianListLoadingFailed, setIsPhysicianListLoadingFailed] = useState<boolean>(false);
    const [physiciansLinkedToLocation, setPhysiciansLinkedToLocation] = useState<any>(physicianDetailsList);
    const [isEdited, setIsEdited] = useState<boolean>(false);

    const getPhysicianList = useCallback(() => {
        const payload = {};
        setIsPhysicianListLoading(true);
        setIsPhysicianListLoaded(false);
        setIsPhysicianListLoadingFailed(false);
        CommonService._facility.getPhysicianList(facilityId, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response.data) {
                    setPhysicianList(response.data);
                    setCompletePhysicianList(response.data);
                }
                setIsPhysicianListLoading(false);
                setIsPhysicianListLoaded(true);
            })
            .catch((error: any) => {
                setIsPhysicianListLoaded(true);
                setIsPhysicianListLoadingFailed(true);
                setIsPhysicianListLoading(false);
            });
    }, [facilityId]);

    useEffect(() => {
        if (facilityId) {
            getPhysicianList();
        }
    }, [getPhysicianList, facilityId])

    const onRemovePhysician = useCallback((physicianId: any) => {
        const payload = {
            "physicianId": physicianId
        };
        CommonService._location.unLinkPhysicianToLocation(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast(response?.message || `Physician has been unlinked from the location`, "success");
                setIsEdited(true);
                setPhysiciansLinkedToLocation((oldState: any) => {
                    const updatedList = _.cloneDeep(oldState);
                    const index = updatedList?.findIndex((item: any) => item.id === physicianId);
                    updatedList.splice(index, 1);
                    return updatedList;
                })
            })
            .catch((error: any) => {
                CommonService._alert.showToast(error?.error || `Error while unlinking the physician from the location`, "error");
            });
    }, [locationId]);

    const handleRemovePhysician = useCallback((item: any) => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: "Are you sure you want to remove this provider/physician?",
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: "Remove",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color:"primary"

            }
        })
            .then(() => {
                onRemovePhysician(item);
            })
            .catch(() => {
            })
    }, [onRemovePhysician]);

    const handleLinkPhysician = useCallback((physicianId: any) => {
        const payload = {
            "physicianIds": physicianId
        };
        setIsLinking(true);
        CommonService._location.linkPhysicianToLocation(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                setIsEdited(true);
                setPhysiciansLinkedToLocation((oldState: any) => {
                    return [...oldState, ...response.data]
                })
                setPhysicianId([]);
                setIsLinking(false);
                CommonService._alert.showToast(response?.message || 'physician has been linked', "success");
            })
            .catch((error: any) => {
                setIsLinking(false);
                CommonService._alert.showToast(error?.message || 'Linking physician to location has been failed', "error");
            })
    }, [locationId]);

    useEffect(() => {
        if (physiciansLinkedToLocation.length > 0) {
            const unLinkedPhysicians = _.differenceBy(completePhysicianList, physiciansLinkedToLocation, 'id');
            setPhysicianList(unLinkedPhysicians);
        } else {
            setPhysicianList(completePhysicianList);
        }
    }, [physiciansLinkedToLocation, completePhysicianList]);

    return (
        <div className={'location-physician-details-edit-component'}>
            <div className={'t-form-controls-wrapper'}>
                <div className={'location-physician-details-edit-component-selector pdd-top-5'}>
                    <SelectDropdownComponent
                        label={"Provider/Physician"}
                        name={"physician"}
                        placeholder={"Search Provider/Physician Name"}
                        required={true}
                        multiple={true}
                        isDataLoaded={isPhysicianListLoaded}
                        isDataLoadingFailed={isPhysicianListLoadingFailed}
                        isDataLoading={isPhysicianListLoading}
                        valueKey={"id"}
                        onUpdate={(value: any) => {
                            setPhysicianId(value)
                        }}
                        value={physicianId}
                        options={physicianList}
                        getOptionLabel={(physician => physician ? CommonService.physicianWithPrefix(physician) : '')}
                    />
                </div>
                <div className={"d-flex justify-content-flex-end"}>
                    <ButtonComponent
                        type="button"
                        color={"secondary"}
                        variant={"outlined"}
                        fullWidth={false}
                        isLoading={isLinking}
                        disabled={physicianId?.length === 0}
                        handleClick={() => {
                            handleLinkPhysician(physicianId);
                        }}
                    >
                        Link to Location
                    </ButtonComponent>
                </div>
                <div className={'form-sub-title mrg-top-20'}>All Location Providers/Physicians</div>
                {physiciansLinkedToLocation.length === 0 && <FeedbackComponent className={"width-50"}>
                    No Provider/Physician have been added to this location.
                </FeedbackComponent>
                }
                {physiciansLinkedToLocation.map((physician: any, index: any) => {
                        return (
                            <div key={index} className={'mrg-bottom-20'}>
                                {
                                    index >= 0 &&
                                    <AccordionComponent
                                        // disabled={!values.physicianId}
                                        isExpanded={physicianList[index]?.isPhysicianExpand}
                                        title={CommonService.physicianWithPrefix(physician)}
                                        subTitle={`(NPI: ${physician.npiNumber})`}
                                        actions={<ButtonComponent
                                            startIcon={<RemoveCircleIcon/>}
                                            variant={"outlined"}
                                            fullWidth={false}
                                            size={"small"}
                                            type={'button'}
                                            color={"primary"}
                                            handleClick={
                                                () => {
                                                    handleRemovePhysician(physician.id)
                                                }
                                            }

                                        >
                                            Remove
                                        </ButtonComponent>}
                                        onChange={(isExpanded) => {
                                            if (isExpanded) {
                                                // setCurrentPhysicianIndex(index);
                                            }
                                            // setFieldValue(`physicianDetails[${index}].isPhysicianExpand`, isExpanded);
                                        }}
                                    >
                                        <div
                                            className="physician-details-section">
                                            <div
                                                className={'d-flex justify-content-space-between mrg-top-15 align-items-center'}>
                                                <div className="form-sub-title">
                                                    Personal Details
                                                </div>
                                            </div>
                                            <PhysicianDetailsComponent
                                                physician={physician}/>
                                        </div>
                                    </AccordionComponent>
                                }

                            </div>
                        )
                    }
                )
                }
            </div>
            <div className={"form-action-section-edit"}>
                <ButtonComponent
                    className={"width-25"}
                    color={"secondary"}
                    type={"button"}
                    variant={"outlined"}
                    handleClick={() => {
                        onEditCancel();
                    }}
                    fullWidth={false}
                >
                    Cancel
                </ButtonComponent>
                <ButtonComponent
                    className={"width-25"}
                    color={"primary"}
                    type={"submit"}
                    variant={"contained"}
                    disabled={isLinking || !isEdited}
                    fullWidth={false}
                    handleClick={() => {
                        if (onUpdateComplete) {
                            onUpdateComplete(physiciansLinkedToLocation);
                        }
                    }
                    }
                >
                    Save
                </ButtonComponent>
            </div>
        </div>
    );

};

export default LocationPhysicianDetailsEditComponent;
